import { chordsData } from '../data/chords';
import * as Tone from 'tone';

// Initialize synthesizer and effects
let synth = null;
let eq = null;
let reverb = null;
let limiter = null;
let compressor = null;
let audioInitialized = false;
let currentPlaybackId = null;
let isPlaying = false;

// Available rhythm patterns (simplified and optimized rhythm patterns)
export const rhythmPatterns = {
  'Even': { 
    name: 'Even', 
    pattern: ['2n'], 
    description: 'Equal duration for each chord' 
  },
  'Waltz': { 
    name: 'Waltz', 
    pattern: ['4n', '4n', '4n'], // Simple 3/4 time, one quarter note per beat
    description: '3/4 time signature with waltz feel' 
  },
  'Syncopated': { 
    name: 'Syncopated', 
    pattern: ['4n', '8n', '4n', '8n'], // More regular syncopated rhythm
    description: 'Emphasizes off-beats for a groovy feel' 
  },
  'Arpeggio': { 
    name: 'Arpeggio', 
    pattern: ['16n'], // Simplified, actual processing done in function
    description: 'Plays chord notes individually in sequence' 
  },
  'Ballad': { 
    name: 'Ballad', 
    pattern: ['2n', '4n', '4n'], // More balanced slow tempo rhythm
    description: 'Slower pattern suitable for ballads' 
  }
};

// 为导出功能提供的节奏模式常量
export const RHYTHM_PATTERNS = rhythmPatterns;

// 优化乐器声音设置，减少杂音和失真
export const instrumentSounds = {
  'Piano': {
    name: 'Piano',
    settings: {
      oscillator: { type: 'sine8' },  // 使用更干净的波形
      envelope: {
        attack: 0.005,
        decay: 0.3,
        sustain: 0.4,
        release: 1.4
      },
      volume: -8 // 降低音量以避免过载
    },
    eqSettings: [
      { frequency: 200, gain: -2, Q: 1 },   // 降低低频部分避免浑浊
      { frequency: 700, gain: 1, Q: 0.8 },  // 轻微提升中频以增强清晰度
      { frequency: 3000, gain: 1.5, Q: 1 }, // 增强高频部分提高亮度
      { frequency: 8000, gain: -3, Q: 2 }   // 降低过高频率避免刺耳
    ]
  },
  'Guitar': {
    name: 'Guitar',
    settings: {
      oscillator: { type: 'fmsine' }, // 更改为更柔和的FM正弦波
      envelope: {
        attack: 0.01,
        decay: 0.2,
        sustain: 0.3,
        release: 0.8
      },
      volume: -10
    },
    eqSettings: [
      { frequency: 120, gain: -3, Q: 0.9 },
      { frequency: 500, gain: 2, Q: 1 },
      { frequency: 1800, gain: 1.5, Q: 1.2 },
      { frequency: 4000, gain: -2, Q: 1.5 }
    ]
  },
  'Synth': {
    name: 'Synth',
    settings: {
      oscillator: { type: 'sine4' }, // 较少的倍频减少杂音
      envelope: {
        attack: 0.05,
        decay: 0.2,
        sustain: 0.6,
        release: 1.0
      },
      volume: -12
    },
    eqSettings: [
      { frequency: 100, gain: -4, Q: 0.8 },
      { frequency: 300, gain: -1, Q: 1 },
      { frequency: 1200, gain: 3, Q: 1 },
      { frequency: 3500, gain: 2, Q: 1.5 }
    ]
  },
  'Organ': {
    name: 'Organ',
    settings: {
      oscillator: { type: 'sine8' },
      envelope: {
        attack: 0.1,
        decay: 0.3,
        sustain: 1.0,
        release: 0.7
      },
      volume: -14 // 更低的音量避免失真
    },
    eqSettings: [
      { frequency: 80, gain: -5, Q: 0.7 },
      { frequency: 600, gain: 2, Q: 1 },
      { frequency: 1500, gain: 3, Q: 1.2 },
      { frequency: 5000, gain: -3, Q: 2 }
    ]
  },
  'Strings': {
    name: 'Strings',
    settings: {
      oscillator: { type: 'sine8' },
      envelope: {
        attack: 0.15,
        decay: 0.3,
        sustain: 0.8,
        release: 2.5
      },
      volume: -11
    },
    eqSettings: [
      { frequency: 150, gain: -2, Q: 0.8 },
      { frequency: 400, gain: 1, Q: 1 },
      { frequency: 1200, gain: 2, Q: 1.5 },
      { frequency: 3800, gain: 1, Q: 1.8 }
    ]
  }
};

// Current selected instrument
let currentInstrument = 'Piano';

// 获取唯一播放标识符
const getPlaybackId = () => {
  return Date.now().toString();
};

// Audio context health check function
const isAudioContextHealthy = () => {
  return Tone.context.state === "running" && !Tone.context.isOffline;
};

// 检查并恢复音频引擎的健康状态
export const checkAndRecoverAudioEngine = async () => {
  if (!isAudioContextHealthy()) {
    try {
      // 尝试恢复上下文
      await Tone.context.resume();
      
      // 检查是否需要重新创建节点
      if (!synth || !reverb || !eq || !compressor || !limiter) {
        return await initAudio();
      }
      
      // 检查节点连接是否正常
      try {
        // 简单测试 - 尝试调整参数
        if (eq) {
          const oldValue = eq.high.value;
          eq.high.value = oldValue;
        }
        if (reverb) {
          const oldValue = reverb.wet.value;
          reverb.wet.value = oldValue;
        }
        return true;
      } catch (e) {
        console.warn("Audio node test failed, needs reinitialization:", e);
        return await initAudio();
      }
    } catch (error) {
      console.error("Failed to recover audio engine:", error);
      return false;
    }
  }
  return true;
};

// 清理音频资源
export const cleanupAudio = () => {
  stopAudio();
  
  // 延迟处理以确保所有音频事件都结束
  setTimeout(() => {
    try {
      if (synth) {
        synth.releaseAll();
        synth.dispose();
        synth = null;
      }
      if (eq) {
        eq.dispose();
        eq = null;
      }
      if (reverb) {
        reverb.dispose();
        reverb = null;
      }
      if (compressor) {
        compressor.dispose();
        compressor = null;
      }
      if (limiter) {
        limiter.dispose();
        limiter = null;
      }
      
      audioInitialized = false;
      isPlaying = false;
      currentPlaybackId = null;
      
    } catch (error) {
      console.warn("Error cleaning up audio resources:", error);
    }
  }, 100);
};

// 优化的音频初始化函数
export const initAudio = async () => {
  if (audioInitialized && isAudioContextHealthy()) return true;
  
  try {
    // 如果已初始化但状态不健康，尝试恢复上下文
    if (audioInitialized) {
      await Tone.context.resume();
      return isAudioContextHealthy();
    }
    
    // 确保所有旧实例被清理
    if (synth) {
      synth.dispose();
      synth = null;
    }
    if (reverb) {
      reverb.dispose();
      reverb = null;
    }
    if (eq) {
      eq.dispose();
      eq = null;
    }
    if (limiter) {
      limiter.dispose();
      limiter = null;
    }
    if (compressor) {
      compressor.dispose();
      compressor = null;
    }
    
    // 重置状态
    audioInitialized = false;
    
    // 等待用户交互启动音频上下文
    await Tone.start();
    
    // 检查上下文状态
    if (Tone.context.state !== "running") {
      await Tone.context.resume();
      
      // 再次验证上下文是否真的启动了
      if (Tone.context.state !== "running") {
        console.warn("Unable to start audio context, may require user interaction");
        return false;
      }
    }
    
    // 创建音频处理链 - 从输出端开始构建
    
    // 1. 创建限制器 - 防止音频峰值导致的失真
    limiter = new Tone.Limiter(-1.5).toDestination();
    
    // 2. 创建压缩器 - 平衡音量
    compressor = new Tone.Compressor({
      threshold: -24,
      ratio: 4,
      attack: 0.005,
      release: 0.1
    }).connect(limiter);
    
    // 3. 创建混响 - 增加音频空间感
    reverb = new Tone.Reverb({
      decay: 1.5,
      wet: 0.2,
      preDelay: 0.01
    }).connect(compressor);
    
    // 4. 创建均衡器 - 塑造音色
    eq = new Tone.EQ3({
      low: 0,
      mid: -1,
      high: -2,
      lowFrequency: 200,  // 增加默认低频截止频率
      highFrequency: 2500 // 增加默认高频截止频率
    }).connect(reverb);
    
    // 确保EQ3的节点已经创建
    if (!eq.low || !eq.mid || !eq.high) {
      console.warn("EQ3 nodes not properly initialized, using default equalizer settings");
    }
    
    // 5. 创建多音合成器 - 产生音色
    synth = new Tone.PolySynth(Tone.Synth, {
      maxPolyphony: 16, // 最大复音数量
      voice: {
        oscillator: {
          type: "sine8",
          partialCount: 8
        },
        envelope: {
          attack: 0.01,
          decay: 0.2,
          sustain: 0.7,
          release: 1.4
        }
      },
      volume: -8
    }).connect(eq);
    
    // 设置默认乐器
    setInstrument('Piano');
    
    // 打开音频节点，确保所有都准备就绪
    await Promise.all([
      synth.ready,
      reverb.ready
    ]);
    
    audioInitialized = true;
    return true;
  } catch (error) {
    console.error("Audio initialization failed:", error);
    
    // 尝试恢复和重置
    try {
      await Tone.context.resume();
      if (synth) synth.releaseAll();
    } catch (e) {
      console.warn("Unable to restore audio context:", e);
    }
    
    return false;
  }
};

// 设置乐器函数 - 更精确的音色设计
export const setInstrument = (instrument = 'Piano') => {
  if (!synth) return false;
  
  try {
    // 确保乐器名称首字母大写
    const instrumentName = instrument.charAt(0).toUpperCase() + instrument.slice(1).toLowerCase();
    
    // 选择乐器配置
    const instrumentConfig = instrumentSounds[instrumentName] || instrumentSounds['Piano'];
    
    // 应用合成器设置
    synth.set({
      oscillator: instrumentConfig.settings.oscillator,
      envelope: instrumentConfig.settings.envelope,
      volume: instrumentConfig.settings.volume
    });
    
    // 应用均衡器设置
    if (eq && instrumentConfig.eqSettings) {
      try {
        // 首先重置 EQ 到中性值
        eq.low.value = 0;
        eq.mid.value = 0;
        eq.high.value = 0;
        
        // 然后应用特定乐器的 EQ 设置
        if (instrumentConfig.eqSettings[0] && eq.low) eq.low.value = instrumentConfig.eqSettings[0].gain;
        if (instrumentConfig.eqSettings[1] && eq.mid) eq.mid.value = instrumentConfig.eqSettings[1].gain;
        if (instrumentConfig.eqSettings[2] && eq.high) eq.high.value = instrumentConfig.eqSettings[2].gain;
        
        // 如果支持，设置频率（注意：某些版本的 Tone.js EQ3 可能不支持直接设置频率）
        if (eq.lowFrequency && instrumentConfig.eqSettings[0]) {
          eq.lowFrequency.value = instrumentConfig.eqSettings[0].frequency;
        }
        if (eq.highFrequency && instrumentConfig.eqSettings[2]) {
          eq.highFrequency.value = instrumentConfig.eqSettings[2].frequency;
        }
      } catch (eqError) {
        console.warn("Error applying EQ settings:", eqError);
      }
    }
    
    // 根据乐器类型调整混响
    if (reverb) {
      switch(instrumentName.toLowerCase()) {
        case 'piano':
          reverb.wet.value = 0.2;
          reverb.decay = 1.5;
          break;
        case 'guitar':
          reverb.wet.value = 0.15;
          reverb.decay = 1.0;
          break;
        case 'strings':
          reverb.wet.value = 0.3;
          reverb.decay = 2.5;
          break;
        case 'organ':
          reverb.wet.value = 0.25;
          reverb.decay = 2.0;
          break;
        case 'synth':
          reverb.wet.value = 0.15;
          reverb.decay = 1.2;
          break;
        default:
          reverb.wet.value = 0.2;
          reverb.decay = 1.5;
      }
    }
    
    currentInstrument = instrumentName;
    return true;
  } catch (error) {
    console.error("Failed to set instrument:", error);
    return false;
  }
};

// Change instrument sound
export const changeInstrument = (instrumentName) => {
  return setInstrument(instrumentName);
};

// Get current instrument
export const getCurrentInstrument = () => {
  return currentInstrument;
};

// 优化的和弦播放函数
export const playChord = async (chordName, duration = '2n') => {
  // 确保音频已初始化
  if (!audioInitialized || !isAudioContextHealthy()) {
    const success = await initAudio();
    if (!success) {
      console.warn('Cannot play: Audio engine initialization failed');
      return false;
    }
  }
  
  try {
    // 查找和弦音符
    const notes = chordsData[chordName];
    if (!notes || !notes.length) {
      console.warn(`Chord not found: ${chordName}`);
      return false;
    }
    
    // 确保音频上下文处于运行状态
    await Tone.context.resume();
    
    // 计算精确的播放时间（稍微提前调度确保精确性）
    const now = Tone.now();
    const scheduleTime = now + 0.05; // 50ms的安全缓冲
    
    // 生成自然的力度变化模式（钢琴真实演奏中，低音通常略重，高音略轻）
    const velocities = notes.map((note, index) => {
      // 创建音符的自然力度曲线 - 低音较重，高音较轻
      const baseVelocity = 0.75; // 基础力度
      const normalizedIndex = index / (notes.length - 1 || 1); // 0到1之间
      const velocityVariation = 0.15; // 力度变化范围
      
      // 映射到基础力度±变化范围之间
      return baseVelocity - normalizedIndex * velocityVariation;
    });
    
    // 为所有音符创建单一的包络 - 确保和弦整体有统一的起音和释放
    notes.forEach((note, index) => {
      synth.triggerAttackRelease(
        note, 
        Tone.Time(duration).toSeconds(), // 确保正确的时值转换 
        scheduleTime, 
        velocities[index]
      );
    });
    
    return true;
  } catch (error) {
    console.error('Error playing chord:', error);
    
    // 尝试恢复音频状态
    if (synth) {
      synth.releaseAll();
    }
    
    return false;
  }
};

// 大幅优化琶音播放函数
export const playArpeggio = async (chord, tempo = 120, octave = 4, direction = 'up') => {
  // 确保音频已初始化
  if (!audioInitialized || !isAudioContextHealthy()) {
    const success = await initAudio();
    if (!success) {
      console.warn('Cannot play arpeggio: Audio engine initialization failed');
      return 0;
    }
  }
  
  // 安全检查 - 确保有有效的和弦数据
  if (!chord || !chordsData[chord] || !chordsData[chord].length) {
    console.warn(`Chord not found: ${chord}, skipping`);
    return 0;
  }
  
  try {
    // 确保音频上下文正常
    await Tone.context.resume();
    
    // 获取和弦音符 - 不添加八度音，因为音符本身已经有完整音高
    const notes = chordsData[chord];
    
    // 根据方向处理音符顺序
    let notesToPlay = [...notes];
    
    if (direction === 'down') {
      // 下行琶音
      notesToPlay.reverse();
    } else if (direction === 'updown' && notes.length > 1) {
      // 上下行琶音 (上行然后下行，但不重复最高音)
      const upNotes = [...notes];
      const downNotes = [...notes].slice(0, -1).reverse();
      notesToPlay = [...upNotes, ...downNotes];
    } else if (direction === 'downup' && notes.length > 1) {
      // 下上行琶音 (下行然后上行，但不重复最低音)
      const downNotes = [...notes].reverse();
      const upNotes = [...notes].slice(1);
      notesToPlay = [...downNotes, ...upNotes];
    } else if (direction === 'random') {
      // 随机顺序琶音
      notesToPlay = [...notes];
      // Fisher-Yates 洗牌算法，确保均匀随机
      for (let i = notesToPlay.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [notesToPlay[i], notesToPlay[j]] = [notesToPlay[j], notesToPlay[i]];
      }
    }
    
    // 计算每个音符之间的间隔（秒）
    const secondsPerBeat = 60 / tempo;
    const sixteenthNote = secondsPerBeat * 0.25; // 十六分音符时值
    const noteSpacing = sixteenthNote; // 使用十六分音符作为音符间隔
    
    // 获取当前时间
    const now = Tone.now();
    // 添加安全的预调度缓冲区
    const startTime = now + 0.05;
    
    // 直接调度每个音符播放，不使用Sequence
    for (let i = 0; i < notesToPlay.length; i++) {
      // 计算精确的播放时间
      const time = startTime + (i * noteSpacing);
      // 音符的精确持续时间（稍短于间隔，避免粘连）
      const noteDuration = noteSpacing * 0.9;
      // 使用稍微变化的力度，使琶音更自然
      const velocity = 0.7 + Math.random() * 0.1;
      
      try {
        // 播放音符
        synth.triggerAttackRelease(
          notesToPlay[i], 
          noteDuration, 
          time, 
          velocity
        );
      } catch (e) {
        console.error("Error playing arpeggio note:", e);
      }
    }
    
    // 计算总持续时间（包括缓冲区）
    const totalDuration = notesToPlay.length * noteSpacing + 0.1;
    
    // 返回总持续时间（秒）
    return totalDuration;
  } catch (error) {
    console.error("Arpeggio playback error:", error);
    // 出错时返回默认持续时间
    return 1;
  }
};

// 修改playProgression函数，修复非琶音模式的发声问题
export const playProgression = async (
  chords, 
  tempo = 120, 
  rhythmPattern = 'Even',
  options = {
    loop: false,
    octave: 4,
    arpeggioDirection: 'up'
  }
) => {
  // 基本验证
  if (!chords || !chords.length) return false;
  
  // 生成新的播放ID
  const playbackId = Date.now().toString();
  currentPlaybackId = playbackId;
  isPlaying = true;
  
  try {
    // 确保音频初始化
    if (!audioInitialized || !isAudioContextHealthy()) {
      const success = await initAudio();
      if (!success) {
        console.warn('Cannot play progression: Audio engine initialization failed');
        isPlaying = false;
        return false;
      }
    }
    
    // 确保音频上下文正常运行
    await Tone.context.resume();
    
    // 获取节奏模式
    const pattern = rhythmPatterns[rhythmPattern]?.pattern || ['2n'];
    
    // 如果使用琶音模式，则使用专门的琶音函数
    if (rhythmPattern === 'Arpeggio') {
      let totalDuration = 0;
      for (let i = 0; i < chords.length; i++) {
        // 检查是否在播放过程中停止了
        if (currentPlaybackId !== playbackId || !isPlaying) {
          break;
        }
        
        // 为每个和弦播放琶音并获取持续时间
        const chordDuration = await playArpeggio(
          chords[i],
          tempo,
          options.octave,
          options.arpeggioDirection || 'up'
        );
        
        totalDuration += chordDuration;
        
        // 等待琶音播放完成
        await new Promise(resolve => setTimeout(resolve, chordDuration * 1000));
      }
      
      // 如果是循环模式且没有被中断，则递归调用
      if (options.loop && currentPlaybackId === playbackId && isPlaying) {
        return playProgression(chords, tempo, rhythmPattern, options);
      }
      
      isPlaying = false;
      return totalDuration;
    }
    
    // ===== 改进的节奏模式播放方法 =====
    
    // 计算四分音符的持续时间（秒）
    const quarterNoteDuration = 60 / tempo;
    
    // 创建一个更可靠的序列播放器
    const chordSequence = [];
    let totalPatternDuration = 0;
    
    // 每个和弦处理中是否需要特殊的强弱处理
    const needsAccentuation = rhythmPattern === 'Waltz';
    
    // 将所有和弦转换为序列事件
    for (let i = 0; i < chords.length; i++) {
      // 获取当前和弦的音符
      const chord = chords[i];
      const notes = chordsData[chord];
      
      if (!notes || !notes.length) {
        console.warn(`Chord not found: ${chord}, skipping`);
        continue;
      }
      
      // 处理节奏模式中的每个音符
      for (let j = 0; j < pattern.length; j++) {
        // 当前节奏单位
        const currentPattern = pattern[j];
        
        // 精确计算音符持续时间
        let noteDuration;
        switch (currentPattern) {
          case '1n': noteDuration = 4 * quarterNoteDuration; break;
          case '2n.': noteDuration = 3 * quarterNoteDuration; break;
          case '2n': noteDuration = 2 * quarterNoteDuration; break;
          case '4n.': noteDuration = 1.5 * quarterNoteDuration; break;
          case '4n': noteDuration = quarterNoteDuration; break;
          case '8n': noteDuration = 0.5 * quarterNoteDuration; break;
          case '16n': noteDuration = 0.25 * quarterNoteDuration; break;
          default: noteDuration = 2 * quarterNoteDuration; // 默认半音符
        }
        
        // 计算力度变化 - 特别是在华尔兹模式下
        // 第一拍加重，其他拍减弱
        let velocityAdjustment = 0;
        if (needsAccentuation) {
          if (j === 0) {
            velocityAdjustment = 0.15; // 第一拍更强
          } else {
            velocityAdjustment = -0.15; // 其他拍更弱
          }
        }
        
        // 添加到序列
        chordSequence.push({
          time: totalPatternDuration,
          chord: chord,
          notes: notes,
          duration: noteDuration,
          velocityAdj: velocityAdjustment
        });
        
        totalPatternDuration += noteDuration;
      }
    }
    
    // 简化的替代方案，使用直接调度而不是Tone.Transport和Part
    // 这种方式更可靠，避免了Tone.Transport的一些问题
    const now = Tone.now() + 0.1; // 添加一点缓冲时间
    let startTime = now;
    
    // 直接调度每个和弦事件
    for (const event of chordSequence) {
      const playTime = now + event.time;
      const baseVelocity = 0.75 + (Math.random() * 0.05);
      const velocity = Math.min(1, Math.max(0.3, baseVelocity + event.velocityAdj));
      
      // 直接触发音符，而不是通过Tone.Part
      try {
        synth.triggerAttackRelease(
          event.notes,
          event.duration * 0.9, // 稍微缩短，避免音符粘连
          playTime,
          velocity
        );
      } catch (error) {
        console.error(`Error playing chord: ${event.chord}`, error);
      }
    }
    
    // 设置一个计时器在播放完成后更新状态
    setTimeout(() => {
      if (currentPlaybackId === playbackId) {
        isPlaying = false;
      }
    }, totalPatternDuration * 1000 + 100); // 额外添加100ms缓冲
    
    // 返回播放序列总时长（秒）
    return totalPatternDuration;
  } catch (error) {
    console.error("Progression playback error:", error);
    isPlaying = false;
    return false;
  }
};

// 改进的停止音频函数
export const stopAudio = () => {
  if (!audioInitialized) return;

  try {
    // 1. 立即释放所有当前音符
    if (synth) {
      synth.releaseAll(0);
    }
    
    // 2. 停止并重置所有 Transport 活动
    Tone.Transport.stop();
    
    // 3. 取消所有调度的事件
    Tone.Transport.cancel(0);
    
    // 4. 清除任何活动的Part对象
    // 注意: Transport.cancel() 应该已处理大部分，这只是以防万一
    Tone.Transport.dispose();
    Tone.Transport.position = 0;
    
    // 5. 重置播放状态
    isPlaying = false;
    currentPlaybackId = null;
    
    // 6. 重启 Transport 以备后续使用
    Tone.Transport.start();
    Tone.Transport.pause();
    
    // 7. 确保音频上下文保持运行状态（以便下次播放不出问题）
    Tone.context.resume().catch(e => console.warn("Unable to restore audio context:", e));
  } catch (error) {
    console.error('Error stopping audio:', error);
    
    // 恢复初始状态
    isPlaying = false;
    currentPlaybackId = null;
    
    // 尝试最基本的重置
    try {
      Tone.Transport.stop();
      Tone.Transport.cancel(0);
      if (synth) synth.releaseAll(0);
    } catch (e) {
      console.warn("Basic reset failed:", e);
    }
  }
}; 